/**
 * Branch Form Modal Component
 * Modal untuk create/edit cabang
 */
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/store';
import { createBranchAsync, updateBranchAsync } from './branchSlice';
import { Branch, BranchCreate, BranchUpdate } from '../../types/branch';

interface BranchFormModalProps {
  onClose: () => void;
  mode: 'create' | 'edit';
  branch?: Branch;
}

const BranchFormModal: React.FC<BranchFormModalProps> = ({ onClose, mode, branch }) => {
  const dispatch = useDispatch<AppDispatch>();

  const [formData, setFormData] = useState({
    code: branch?.code || '',
    name: branch?.name || '',
    address: branch?.address || '',
    phone: branch?.phone || '',
    is_active: branch?.is_active ?? true,
    printer_paper_width: branch?.printer_paper_width || 58,
    printer_header: branch?.printer_header || '',
    printer_footer: branch?.printer_footer || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              type === 'number' ? parseInt(value) || 0 : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (mode === 'create') {
        const createData: BranchCreate = {
          code: formData.code.toUpperCase(),
          name: formData.name,
          address: formData.address || undefined,
          phone: formData.phone || undefined,
        };
        await dispatch(createBranchAsync(createData)).unwrap();
      } else if (mode === 'edit' && branch) {
        const updateData: BranchUpdate = {
          name: formData.name,
          address: formData.address || undefined,
          phone: formData.phone || undefined,
          is_active: formData.is_active,
          printer_paper_width: formData.printer_paper_width,
          printer_header: formData.printer_header || undefined,
          printer_footer: formData.printer_footer || undefined,
        };
        await dispatch(updateBranchAsync({ id: branch.id, data: updateData })).unwrap();
      }
      onClose();
    } catch (err: any) {
      setError(err || 'Terjadi kesalahan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            {mode === 'create' ? '➕ Tambah Cabang' : '✏️ Edit Cabang'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* Branch Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kode Cabang <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                disabled={mode === 'edit'}
                maxLength={10}
                required
                placeholder="Contoh: 74PL, GAP, RJS"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 uppercase"
              />
              <p className="text-xs text-gray-500 mt-1">
                Kode unik cabang, maks 10 karakter
              </p>
            </div>

            {/* Branch Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nama Cabang <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                maxLength={100}
                required
                placeholder="Contoh: 74PhoneLabs Tegal"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alamat
              </label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                maxLength={255}
                rows={3}
                placeholder="Alamat lengkap cabang"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telepon
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                maxLength={20}
                placeholder="Contoh: 0283-1234567"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Printer Settings */}
            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-gray-900 mb-3">⚙️ Pengaturan Printer</h3>
              
              <div className="space-y-4">
                {/* Paper Width */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lebar Kertas
                  </label>
                  <select
                    name="printer_paper_width"
                    value={formData.printer_paper_width}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={58}>58mm</option>
                    <option value={80}>80mm</option>
                  </select>
                </div>

                {/* Printer Header */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Header Struk (Custom)
                  </label>
                  <textarea
                    name="printer_header"
                    value={formData.printer_header}
                    onChange={handleChange}
                    rows={2}
                    placeholder="Teks tambahan di header struk"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Printer Footer */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Footer Struk
                  </label>
                  <textarea
                    name="printer_footer"
                    value={formData.printer_footer}
                    onChange={handleChange}
                    rows={2}
                    placeholder="Contoh: Garansi Service 7 Hari, Terima Kasih"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Active Status (Edit only) */}
            {mode === 'edit' && (
              <div className="border-t pt-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Cabang Aktif
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex gap-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Menyimpan...' : mode === 'create' ? 'Simpan' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BranchFormModal;
